import React, { useState, useEffect } from "react";
import SEO from "../seo";
const General = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "store-information",
        "location-address",
        "currency-time",
        "action",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="General Settings | xPage Help Center"
        description="Learn how to configure your General Settings in xPage. Manage store information, address and location details, currency, time zone, and essential store preferences."
        keywords="xPage, general settings, store information, store name, store email, store phone, company name, store address, location settings, currency settings, time zone, configure store"
        canonical="https://help.xpage.ai/help/settings/General"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            General Settings
          </h1>
          <p className="text-sm text-systemLabel">
            The General Settings section is where you configure the foundational
            information for your store. Here, you can:
          </p>
        </header>

        {/* Store Information Section */}
        <section id="store-information" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Store Information
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Store Name
                </h3>
                <p className="text-sm text-systemLabel">Set your Store Name</p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Store Phone Number
                </h3>
                <p className="text-sm text-systemLabel">
                  Add or update your Store Phone Number
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Store Email
                </h3>
                <p className="text-sm text-systemLabel">
                  Configure your Store Email
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Company Name
                </h3>
                <p className="text-sm text-systemLabel">
                  Define your Company Name
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Location & Address Section */}
        <section id="location-address" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Location & Address
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Country
                </h3>
                <p className="text-sm text-systemLabel">Select your Country</p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Address
                </h3>
                <p className="text-sm text-systemLabel">Enter your Address</p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  City
                </h3>
                <p className="text-sm text-systemLabel">Specify your City</p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  ZIP Code
                </h3>
                <p className="text-sm text-systemLabel">Add your ZIP Code</p>
              </div>
            </div>
          </div>
        </section>

        {/* Currency & Time Section */}
        <section id="currency-time" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Currency & Time
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Currency
                </h3>
                <p className="text-sm text-systemLabel">
                  Choose your store Currency
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Time Zone
                </h3>
                <p className="text-sm text-systemLabel">
                  Set the Time Zone for your store operations
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Action Section */}
        <section id="action" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Action
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Save Changes
                </h3>
                <p className="text-sm text-systemLabel">
                  Save Changes to update and apply all settings
                </p>
              </div>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                This section ensures your store is correctly configured for
                communications, billing, reporting, and location-specific
                operations.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#store-information"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "store-information"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Store Information
              </a>
            </li>
            <li>
              <a
                href="#location-address"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "location-address"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Location & Address
              </a>
            </li>
            <li>
              <a
                href="#currency-time"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "currency-time"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Currency & Time
              </a>
            </li>
            <li>
              <a
                href="#action"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "action"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Action
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default General;
